// 업로드 전 클라이언트 측 이미지 다운스케일.
// Workers 는 WASM 컴파일 차단/CPU 50ms 제약이라 서버 리사이즈가 비현실적이고 Cloudflare Images 는 유료다.
// 그래서 관리자 브라우저에서 canvas 로 미리 축소+재인코딩해 R2 저장/전송량을 줄인다(비용 0).
// 폰 카메라 원본(수 MB/4000px)을 화면에 충분한 크기로 캡한다. 실패/미지원 시 원본을 그대로 올린다(서버가 재검증).
//
// Safari(아이폰/아이패드) 주의 3가지 - 실제 업로드 실패 사고의 원인이었다:
//  1) Safari 는 웹에서 HEIC 디코딩이 불가능하다. 파일 input 의 accept 에 heic 이 없으면
//     iOS 가 "사진 보관함" 경로에서만 JPEG 로 자동 변환해 준다. "파일 선택"(파일 앱) 경로로
//     고르면 원본 HEIC 이 그대로 넘어와 디코딩이 실패한다 -> libheif(WASM)로 직접 디코딩한다.
//  2) 그 디코더는 HEIC 을 실제로 만났을 때만 동적 import 한다. 평소 업로드(jpeg/png)는
//     내려받지도 않으므로 용량/속도에 영향이 없다.
//  3) Safari 의 canvas.toBlob 은 webp 인코딩을 지원하지 않고 스펙에 따라 PNG 로 폴백한다.
//     이때 파일 타입을 "image/webp" 로 라벨링하면 서버의 매직바이트 검사(CONTENT_MISMATCH)에 걸려
//     업로드가 거절된다. 반드시 toBlob 이 실제 돌려준 blob.type 으로 파일을 만들어야 한다.

import type { ImageUploadKind } from "@oo/shared";

// 용도별 긴 변 상한(px). 카테고리 아이콘은 작게, 배너/상세는 크게.
const MAX_EDGE: Record<ImageUploadKind, number> = {
  category: 512,
  "product-main": 1280,
  "product-detail": 1600,
  banner: 1600,
};

// 인코딩 품질(0~1). 0.82 면 육안 손실 거의 없이 용량을 크게 줄인다.
const ENCODE_QUALITY = 0.82;

// 이미 충분히 작으면(축소 불필요 + 용량 작음) 재인코딩하지 않는다.
const SKIP_REENCODE_MAX_BYTES = 600_000;

// HEIC/HEIF(아이폰 원본 포맷)는 서버가 허용하지 않으므로 재인코딩 없이는 업로드가 불가능하다.
const HEIC_MIME_RE = /^image\/(heic|heif)$/u;
// 파일 앱 경로로 고르면 iOS 가 type 을 비우거나 octet-stream 으로 주기도 한다. 그때는 확장자로 판별한다.
const HEIC_EXT_RE = /\.(heic|heif)$/iu;
// 처리 대상 포맷: 서버 허용(jpeg/png/webp) + HEIC/HEIF. 그 외(gif/svg 등)는 원본 그대로(서버 판단).
const PROCESSABLE_RE = /^image\/(jpeg|png|webp|heic|heif)$/u;

// 확장자까지 본 HEIC 판별. type 이 신뢰할 수 없는 iOS 경로를 커버한다.
function isHeicLike(file: File): boolean {
  return HEIC_MIME_RE.test(file.type) || HEIC_EXT_RE.test(file.name);
}

// libheif(WASM) 로 HEIC 을 디코딩한다. 번들에 상시 포함하면 관리자 첫 로딩이 느려지므로
// HEIC 을 실제로 만난 순간에만 동적 import 한다(Vite 가 별도 청크로 분리).
async function decodeHeicToBitmap(file: File): Promise<ImageBitmap | null> {
  try {
    const { heicTo } = await import("heic-to");
    return await heicTo({ blob: file, type: "bitmap", options: { imageOrientation: "from-image" } });
  } catch {
    return null;
  }
}

// 이미지 디코딩. Safari 구버전이 createImageBitmap 옵션(imageOrientation)에서 던질 수 있어
// 옵션 포함 -> 옵션 없이 2단계로 시도한다(옵션 없는 경로도 최신 브라우저는 EXIF 회전을 반영한다).
// 브라우저가 못 읽는 HEIC 이면 마지막으로 WASM 디코더에 넘긴다.
async function decodeToBitmap(file: File): Promise<ImageBitmap | null> {
  try {
    // EXIF 회전을 반영해 폰 사진이 옆으로 눕지 않게 한다.
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    try {
      return await createImageBitmap(file);
    } catch {
      // 네이티브 디코딩 실패. HEIC 계열이면 WASM 디코더로 한 번 더 시도한다.
      return isHeicLike(file) ? await decodeHeicToBitmap(file) : null;
    }
  }
}

// canvas -> Blob 인코딩. 요청 포맷을 브라우저가 지원하지 않으면 실제 반환 타입이 달라질 수 있으므로
// 호출자는 반드시 반환 blob.type 을 신뢰해야 한다(요청 타입 아님).
function encodeCanvas(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, type, ENCODE_QUALITY);
  });
}

// MIME -> 파일 확장자(파일명 표기용).
const EXT_BY_MIME: Record<string, string> = {
  "image/webp": "webp",
  "image/jpeg": "jpg",
  "image/png": "png",
};

// 업로드 직전 이미지를 축소한다. 처리 대상이 아니거나 실패하면 원본 File 을 그대로 반환한다.
// 단, HEIC/HEIF 인데 이 브라우저에서 변환이 불가능하면 서버가 어차피 거절하므로 명확한 오류를 던진다.
export async function downscaleImage(file: File, kind: ImageUploadKind): Promise<File> {
  const heicLike = isHeicLike(file);
  // iOS 일부는 type 을 비우거나 octet-stream 으로 보낸다. 빈 type/HEIC 확장자도 디코딩을 시도해 살린다.
  if (file.type !== "" && !heicLike && !PROCESSABLE_RE.test(file.type)) {
    return file;
  }
  // HEIC/HEIF(또는 type 미상)는 서버가 거절하므로 작더라도 무조건 재인코딩해야 한다.
  const mustReencode = file.type === "" || heicLike;

  const bitmap = await decodeToBitmap(file);
  if (!bitmap) {
    // WASM 디코더까지 실패한 HEIC. 올려봐야 서버가 거절하므로 사용자에게 바로 안내한다.
    if (heicLike) {
      throw new Error(
        "아이폰 원본(HEIC) 사진을 변환하지 못했습니다. 사진 앱에서 다시 선택하거나 JPEG 로 내보내 주세요.",
      );
    }
    return file; // 그 외 디코딩 실패는 원본 업로드(서버가 재검증).
  }

  const maxEdge = MAX_EDGE[kind] ?? 1280;
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));

  // 축소 불필요 + 이미 작은 용량이면 그대로(불필요한 재인코딩 방지). 단 HEIC 등 재인코딩 필수 포맷은 예외.
  if (!mustReencode && scale === 1 && file.size <= SKIP_REENCODE_MAX_BYTES) {
    bitmap.close();
    return file;
  }

  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  // 1차: webp 시도. 미지원 브라우저(Safari)는 null 또는 PNG 폴백을 돌려줄 수 있다.
  let blob = await encodeCanvas(canvas, "image/webp");
  if (!blob || blob.type !== "image/webp") {
    // 2차: 포맷을 명시해 폴백한다. 사진(jpeg/heic 원본, 투명도 없음)은 jpeg 로 작게,
    // png 등 투명도가 있을 수 있는 원본은 png 로 보존한다.
    const fallbackType = heicLike || file.type === "image/jpeg" ? "image/jpeg" : "image/png";
    blob = await encodeCanvas(canvas, fallbackType);
  }
  if (!blob || !EXT_BY_MIME[blob.type]) {
    // 인코딩 실패(또는 서버가 모르는 타입) - HEIC 는 원본 업로드가 불가능하므로 안내, 그 외는 원본 유지.
    if (mustReencode) {
      throw new Error("이미지 변환에 실패했습니다. 다른 사진으로 다시 시도해 주세요.");
    }
    return file;
  }
  // 재인코딩이 오히려 더 크면(이미 잘 압축된 작은 이미지) 원본 유지. 단 재인코딩 필수 포맷은 결과를 유지한다.
  if (!mustReencode && scale === 1 && blob.size >= file.size) {
    return file;
  }

  const baseName = file.name.replace(/\.[^.]+$/u, "") || "image";
  // 파일 타입/확장자는 요청 포맷이 아니라 "실제 인코딩 결과(blob.type)" 기준. 서버가 매직바이트로
  // 선언 타입과 대조(CONTENT_MISMATCH)하므로 여기가 어긋나면 업로드가 거절된다.
  return new File([blob], `${baseName}.${EXT_BY_MIME[blob.type]}`, { type: blob.type });
}
